import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Search, X } from 'lucide-react';

import SearchBox from '../SearchBox';
import useUniversalSearchbarHook from '../useUniversalSearchbarHook';

const UniversalSearchbarMobile = ({ isTransparent, scroll }: any) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    inputRef,
    inputValue,
    handleInputChange,
    handleLinkClick,
    matchedResults,
    filteredCollegeData,
    filteredNewsData,
    filteredExamData,
    courseData,
    topSuggestions,
    handleTopSuggestionClick,
  } = useUniversalSearchbarHook();

  const closeMenu = () => {
    setIsMenuOpen(false);
    handleLinkClick();
  };

  return (
    <div className="relative lg:hidden">
      <button name="Search bar" aria-label="search bar" className="p-1 mt-1" onClick={() => setIsMenuOpen(true)}>
        <Search className={`size-4 ${isTransparent && scroll ? 'text-white' : 'text-black'} mr-1 `} />
      </button>

      <div>
        {isMenuOpen && (
          <div>
            <div className="fixed inset-0 bg-white z-50 flex flex-col">
              <div className="absolute top-0 left-0 right-0 p-4 flex items-center w-full gap-2">
                <div className="border rounded-md px-2 flex-1 flex items-center">
                  <Search className={`size-4`} />
                  <input
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    className="text-gray-500 font-inter text-sm flex-1 py-2.5 outline-none px-2"
                    placeholder="Search for colleges, exams, courses and more..."
                  />
                </div>
                <button
                  name="close"
                  aria-label="close"
                  onClick={closeMenu}
                  className="text-gray-500 font-semibold basis-1/12 text-right"
                >
                  <X className="size-6" />
                </button>
              </div>

              {/* Search results area */}
              <div className="px-3 mt-20 pb-10 overflow-y-auto max-h-[calc(100vh-4rem)]">
                {inputValue?.toLowerCase()?.startsWith('top') && topSuggestions.length > 0 ? (
                  <div>
                    <div className="text-primary font-semibold text-base lg:text-xl font-inter">Top Suggestions</div>
                    <div className="font-inter flex flex-col text-xs lg:text-sm leading-tight tracking-tighter max-h-full overflow-y-auto mt-2">
                      {/* Group by type: stream and course */}
                      {topSuggestions.filter((s: any) => s.type === 'stream').length > 0 && (
                        <div>
                          {topSuggestions
                            .filter((suggestion: any) => suggestion.type === 'stream')
                            .map((suggestion: any) => (
                              <button
                                key={suggestion?.label}
                                onClick={() => {
                                  handleTopSuggestionClick(suggestion);
                                  closeMenu();
                                }}
                                className="flex items-center gap-x-2 justify-between hover:bg-gray-100 p-2 rounded-lg transition-colors duration-150 w-full text-left"
                              >
                                <span className="text-xs lg:text-base font-medium text-primary-gray hover:text-primary">
                                  {suggestion?.label}
                                </span>
                                <span className="text-xs text-gray-500 font-medium">Colleges Listing</span>
                              </button>
                            ))}
                        </div>
                      )}
                      {topSuggestions.filter((s: any) => s.type === 'course').length > 0 && (
                        <div>
                          {topSuggestions
                            .filter((suggestion: any) => suggestion.type === 'course')
                            .map((suggestion: any) => (
                              <button
                                key={suggestion?.label}
                                onClick={() => {
                                  handleTopSuggestionClick(suggestion);
                                  closeMenu();
                                }}
                                className="flex items-center gap-x-2 justify-between hover:bg-gray-100 p-2 rounded-lg transition-colors duration-150 w-full text-left"
                              >
                                <span className="text-xs lg:text-base font-medium text-primary-gray hover:text-primary">
                                  {suggestion?.label}
                                </span>
                                <span className="text-xs text-gray-500 font-medium">Colleges Listing</span>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : inputValue.length > 2 ? (
                  <div>
                    <div className="text-primary font-semibold text-base lg:text-xl font-inter">Search Results</div>
                    <div className="font-inter flex flex-col gap-1.5 gap-y-3 mt-3 text-xs lg:text-sm leading-tight tracking-tighter max-h-full overflow-y-auto">
                      {matchedResults.length > 0 ? (
                        matchedResults.map((item: any) => {
                          const collegeNavbar =
                            item?.college_navbars?.[0]?.title?.replace(/\s+/g, '-')?.toLowerCase() || 'college-info';
                          const isOnlineCollege = item?.is_online_college;
                          const collegeUrl = isOnlineCollege
                            ? `/colleges/${item?.college_url}`
                            : `/colleges/${item?.college_url}/${collegeNavbar}`;

                          const examNavbar =
                            item?.exam_navbars?.[0]?.title?.replace(/\s+/g, '-')?.toLowerCase() || 'exam-info';
                          const courseNavbar =
                            item?.course_navbars?.[0]?.title?.replace(/\s+/g, '-')?.toLowerCase() || 'course-info';

                          let url = '#';
                          if (item?.college_url) {
                            url = collegeUrl;
                          } else if (item?.exam_url) {
                            url = `/exams/${item.exam_url}/${examNavbar}`;
                          } else if (item?.course_url) {
                            url = `/courses/${item.course_url}/${courseNavbar}`;
                          } else if (item?.institute_url) {
                            url = `/institutes/${item.institute_url}`;
                          }

                          const name =
                            item.college_name || item.exam_name || item.course_name || item.institute_name || item.name;
                          const imageUrl = item.logo?.url || item.icon?.url || item?.featured_image?.url;
                          let itemType = '';
                          if (item.college_url) {
                            itemType = 'College';
                          } else if (item.exam_url) {
                            itemType = 'Exam';
                          } else if (item?.course_url) itemType = 'Course';
                          else if (item?.institute_url) itemType = 'Institute';
                          else if (item?.url) itemType = 'News';

                          return (
                            <div key={item.id} className="flex items-center gap-x-2 justify-between">
                              <div className="flex items-center gap-x-3">
                                {imageUrl && (
                                  <Image
                                    width={200}
                                    height={200}
                                    src={imageUrl}
                                    alt={name}
                                    className="w-5 h-5 object-cover object-center rounded border"
                                  />
                                )}
                                <div className="flex items-center">
                                  <h2 className="line-clamp-2 text-xs lg:text-base font-medium">
                                    <Link
                                      href={url}
                                      className="text-primary-gray hover:text-primary"
                                      onClick={closeMenu}
                                    >
                                      {name}
                                    </Link>
                                  </h2>
                                </div>
                              </div>
                              <p className="text-xs lg:text-sm text-gray-500 font-medium">{itemType}</p>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-center text-gray-600">No results found</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <SearchBox
                    inputValue={inputValue}
                    filteredCollegeData={filteredCollegeData}
                    handleLinkClick={closeMenu}
                    filteredExamData={filteredExamData}
                    filteredCourseData={courseData}
                    filteredNewsData={filteredNewsData}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalSearchbarMobile;
